import { computed, observable, makeObservable } from 'mobx';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../commonv3/api/RemoteResourceV3';
import { WorkingLessonIndexView } from '../../../types/workingLessons';
import { WorkingLessonApis } from '../../../apis/workingLessons';
import { GlobalStatesV3 } from '../../../../commonv3/states/GlobalStatesV3';
import { ReservationListState } from '../../../components/organisms/home/states/ReservationListState';
import { SessionListState } from '../../../components/organisms/sessions/states/SessionListState';
import { NotificationApis } from '../../../apis/notifications';
import { Notification } from '../../../types/notifications';
import { MonthlyGoalCardState } from '../../../components/molecules/goals/home/states/MonthlyGoalCardState';
import { V3Routes } from '../../../routes';
import moment from 'moment-timezone';
import { EventListState } from '../../../components/organisms/events/states/EventListState';
import { EventCategory, SheEventClient } from '../../../../commonv3/events/SheEventClient';
import { LocationApis } from '../../../apis/locations';
import { SheLocation } from '../../../types/location';
import { SubmitState } from '../../../../commonv3/states/SubmitState';
import { getLocationSelector, getOnlineSelector } from '../../../components/molecules/forms/LocationSelector';
import { SessionTemplateApis } from '../../../apis/sessionTemplates';
import { SessionTemplate } from '../../../../adminv3/types/session';
import { SessionType } from '../../../types/sessions';
import { MembershipPlanGroupKey } from '../../../types/membershipPlans';

export class HomePageState {
  workingLessons: RemoteResourceV3<WorkingLessonIndexView[]> = RemoteResourceV3.loading();
  notification: RemoteResourceV3<Notification> = RemoteResourceV3.loading();
  locations: RemoteResourceV3<SheLocation[]> = RemoteResourceV3.loading();
  sessionTemplates: RemoteResourceV3<SessionTemplate[]> = RemoteResourceV3.loading();
  reservationList: ReservationListState = new ReservationListState();
  monthlyGoals: MonthlyGoalCardState = new MonthlyGoalCardState();
  sessionList: SessionListState = new SessionListState();
  eventList: EventListState = new EventListState();
  initState = new SubmitState();
  seeMoreLearningLessons = false;

  constructor() {
    makeObservable(this, {
      workingLessons: observable,
      notification: observable,
      locations: observable,
      sessionTemplates: observable,
      reservationList: observable,
      monthlyGoals: observable,
      seeMoreLearningLessons: observable,
      loading: computed,
    });
  }

  async init() {
    const { routing, currentUser } = GlobalStatesV3;
    await this.initState.operate(async () => {
      await Promise.all([
        this.reloadWorkingLessons(),
        this.reloadCourseCompletionNotification(),
        this.reloadLocations(),
        currentUser.reloadUser(),
        this.reservationList.init(),
        this.monthlyGoals.init(),
        this.sessionList.init(),
        this.eventList.init(),
      ]);
      if (currentUser.isV3Member() && !currentUser.preference.completedIntroduction) {
        routing.push(V3Routes.welcomeIntroduction());
      }
      const { isHomeLocationSet, isCurrentLocationOnline, locationId } = currentUser.homeLocation;
      this.eventList.setSelectedLocationSelectors(
        isHomeLocationSet && !isCurrentLocationOnline
          ? [getLocationSelector(locationId), getOnlineSelector()]
          : [getOnlineSelector()]
      );
    });
  }
  get loading() {
    return this.initState.submitting;
  }

  async reloadWorkingLessons() {
    await updateRemoteResourceV3(
      this.workingLessons,
      (v) => (this.workingLessons = v),
      () => WorkingLessonApis.findAll()
    );
  }

  async reloadCourseCompletionNotification() {
    await updateRemoteResourceV3(
      this.notification,
      (v) => (this.notification = v),
      () => NotificationApis.peekNotification()
    );
  }

  async reloadLocations() {
    await updateRemoteResourceV3(
      this.locations,
      (v) => (this.locations = v),
      () => LocationApis.findAll()
    );
  }

  async reloadSessionTemplates(sessionType: SessionType, membershipPlanGroupKey: MembershipPlanGroupKey) {
    await updateRemoteResourceV3(
      this.sessionTemplates,
      (v) => (this.sessionTemplates = v),
      () =>
        SessionTemplateApis.findAll({
          session_type: sessionType,
          current_service: membershipPlanGroupKey,
        })
    );
  }

  async closeCourseCompletionNotification() {
    const notification = this.notification.getOrThrow();
    await NotificationApis.removeNotification(notification);
    this.reportNotificationEvent(notification);
    this.reloadCourseCompletionNotification();
  }

  toggleSeeMoreLearningLessons(): void {
    this.seeMoreLearningLessons = !this.seeMoreLearningLessons;
  }

  reportNotificationEvent(notification: Notification) {
    SheEventClient.reportEvent(EventCategory.NOTIFICATION, 'notification_closed', notification);
  }

  async completeIntroduction() {
    await GlobalStatesV3.currentUser.preference.update({
      introduction_completed_at: moment().format('YYYY-MM-DD HH:mm'),
    });
    if (GlobalStatesV3.currentUser.preference.coachingTooltipClosed) {
      GlobalStatesV3.routing.push(V3Routes.home());
    } else {
      GlobalStatesV3.routing.push(V3Routes.reserveCoaching());
    }
  }

  async closeCoachingTooltip() {
    await GlobalStatesV3.currentUser.preference.update({
      coaching_tooltip_closed: true,
    });
  }
}
